import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import CloseIcon from 'src/assets/svg/followup/changelog/Close.svg';
import EditIcon from 'src/assets/svg/followup/changelog/Edit.svg';
import OpenIcon from 'src/assets/svg/followup/changelog/Open-InProgress.svg';
import ReassignIcon from 'src/assets/svg/followup/changelog/Reassign.svg';

import type { ChangelogRowIcon } from './ChangelogRow';
import ChangelogRow from './ChangelogRow';
import { getEntryChangeType } from './getEntryChangeType';
import type { ChangelogEntry } from './types';

interface ChangelogUpdateProps {
  entry: ChangelogEntry;
}

const ChangelogUpdate: FC<ChangelogUpdateProps> = ({ entry }) => {
  const { t } = useTranslation('followUp');

  const date = useMemo(
    () => new Date(Date.parse(entry.timestamp || '')),
    [entry],
  );

  const changeType = getEntryChangeType(entry);

  let description: ReactNode;
  let icon: ChangelogRowIcon;
  switch (changeType) {
    case 'followUpCallCreated':
      description = t('changelog.followUpCallCreated');
      icon = {
        src: EditIcon,
        alt: t('changelog.iconAlt.edit'),
      };
      break;

    case 'followUpCallEdited':
      description = t('changelog.followUpCallEdited');
      icon = {
        src: EditIcon,
        alt: t('changelog.iconAlt.edit'),
      };
      break;

    case 'changedOwnerTo':
      description = (
        <Trans
          i18nKey="followUp:changelog.changedOwnerTo"
          values={{ newOwner: entry.changes.followUpOwner.after }}
          components={{
            wrap: (
              <Typography
                component="span"
                color="black"
                sx={{ fontWeight: 'bold' }}
              />
            ),
          }}
        />
      );
      icon = {
        src: ReassignIcon,
        alt: t('changelog.iconAlt.reassign'),
      };
      break;

    case 'closed':
      description = t('changelog.closed');
      icon = {
        src: CloseIcon,
        alt: t('changelog.close'),
      };
      break;

    case 'reopened':
      description = t('changelog.reopened');
      icon = {
        src: OpenIcon,
        alt: t('changelog.reopen'),
      };
      break;

    case 'progressed':
      description = t('changelog.progressed');
      icon = {
        src: OpenIcon,
        alt: t('changelog.progress'),
      };
      break;

    case 'ticketEdited':
      description = t('changelog.ticketEdited');
      icon = {
        src: EditIcon,
        alt: t('changelog.iconAlt.edit'),
      };
      break;

    case 'ticketCreated':
      description = t('changelog.ticketCreated');
      icon = {
        src: OpenIcon,
        alt: t('changelog.iconAlt.create'),
      };
      break;

    case 'customFieldValueCreated':
      description = (
        <Trans
          i18nKey="followUp:changelog.customFieldValueCreated"
          values={{ fieldName: entry.additionalData?.name }}
          components={{
            wrap: (
              <Typography
                component="span"
                color="black"
                sx={{ fontWeight: 'bold' }}
              />
            ),
          }}
        />
      );
      icon = {
        src: EditIcon,
        alt: t('changelog.iconAlt.edit'),
      };
      break;

    case 'customFieldValueEdited':
      description = (
        <Trans
          i18nKey="followUp:changelog.customFieldValueEdited"
          values={{ fieldName: entry.additionalData?.name }}
          components={{
            wrap: (
              <Typography
                component="span"
                color="black"
                sx={{
                  fontWeight: 'bold',
                }}
              />
            ),
          }}
        />
      );
      icon = {
        src: EditIcon,
        alt: t('changelog.iconAlt.edit'),
      };
      break;

    case null:
    default:
      description = t('changelog.unknownAction');
      icon = {
        src: OpenIcon,
        alt: t('changelog.iconAlt.Open'),
      };
  }

  const authorName = useMemo(() => {
    if (changeType === 'ticketCreated' || changeType === null) return '';
    return entry.actor || t('changelog.deletedUser');
  }, [entry, t]);

  return (
    <ChangelogRow
      authorName={authorName}
      date={date}
      actionDescription={description}
      icon={icon}
    />
  );
};

export default ChangelogUpdate;
